import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService, AuthLoginProviderResponse } from '../../../security/authentication.service';
import {
  faArrowRight,
  faArrowUpRightFromSquare,
  faEnvelope,
  faFileAlt,
  faInfoCircle,
  faMobileScreen,
  faMobileScreenButton,
  faShieldHalved,
  faSignInAlt,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {PageLoadingService} from "../../../services/page-loading.service";
import { finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MFA_RE_ENROLLMENT_GUIDE_URL } from '../../../documentation-links';

interface AuthenticatorAppOption {
  name: string;
  appleStoreUrl: string;
  googlePlayUrl: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [
    FontAwesomeModule,
    ReactiveFormsModule,
    RouterLink,
  ],
})
export class LoginComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pageLoadingService = inject(PageLoadingService);

  protected readonly faArrowRight = faArrowRight;
  protected readonly faEnvelope = faEnvelope;
  protected readonly faFileAlt = faFileAlt;
  protected readonly faInfoCircle = faInfoCircle;
  protected readonly faMobileScreen = faMobileScreen;
  protected readonly faMobileScreenButton = faMobileScreenButton;
  protected readonly faShieldHalved = faShieldHalved;
  protected readonly faSignInAlt = faSignInAlt;
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected readonly firstTimeSignInInstructionsUrl =
    'https://www.sandiegocounty.gov/probation/docs/CRD_First-Time_Sign-In_Instructions.pdf';
  protected readonly mfaReEnrollmentGuideUrl = MFA_RE_ENROLLMENT_GUIDE_URL;
  protected readonly authenticatorApps: AuthenticatorAppOption[] = [
    {
      name: 'Auth0 Guardian',
      appleStoreUrl: 'https://apps.apple.com/us/app/auth0-guardian/id1093447833',
      googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.auth0.guardian',
    },
    {
      name: 'Authy',
      appleStoreUrl: 'https://apps.apple.com/us/app/twilio-authy/id494168017',
      googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.authy.authy',
    },
    {
      name: 'Google Authenticator',
      appleStoreUrl: 'https://apps.apple.com/us/app/google-authenticator/id388497605',
      googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
    },
    {
      name: 'Microsoft Authenticator',
      appleStoreUrl: 'https://apps.apple.com/us/app/microsoft-authenticator/id983156458',
      googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.azure.authenticator',
    },
  ];
  loginForm = this.formBuilder.nonNullable.group({
    email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
  });

  isSubmitting = false;
  accountNotFound = false;
  passwordResetEmailSent = false;

  ngOnInit(): void {
    this.pageLoadingService.isContentPageLoading = false;
    this.emailControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.accountNotFound = false);

    if (this.authenticationService.currentUserValue?.username) {
      void this.router.navigate(['/']);
      return;
    }
  }

  get emailControl() {
    return this.loginForm.controls.email;
  }

  submitForm(): void {
    this.accountNotFound = false;
    this.passwordResetEmailSent = false;
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    this.authenticationService.storeReturnUrl(returnUrl);
    this.isSubmitting = true;

    const submittedEmail = this.emailControl.value;
    this.authenticationService.resolveLoginProvider(submittedEmail)
      .pipe(finalize(() => this.isSubmitting = false))
      .subscribe({
        next: response => this.handleLoginProviderResponse(response),
        error: error => this.handleLoginProviderError(error, submittedEmail),
      });
  }

  protected redirectTo(url: string): void {
    window.location.href = url;
  }

  protected showLoginForm(): void {
    this.passwordResetEmailSent = false;
  }

  protected getAuthenticatorAppStoreUrl(app: AuthenticatorAppOption): string {
    return this.prefersAppleAppStore()
      ? app.appleStoreUrl
      : app.googlePlayUrl;
  }

  private prefersAppleAppStore(): boolean {
    const userAgent = window.navigator.userAgent || '';
    const platform = window.navigator.platform || '';
    const maxTouchPoints = window.navigator.maxTouchPoints || 0;

    return /iPad|iPhone|iPod/i.test(userAgent)
      || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1)
      || /Mac/i.test(platform);
  }

  private handleLoginProviderResponse(response: AuthLoginProviderResponse): void {
    if (response.action === 'ACCOUNT_INACTIVE') {
      void this.router.navigate(['/account-inactive']);
      return;
    }

    if (response.action === 'PASSWORD_RESET_EMAIL_SENT') {
      this.passwordResetEmailSent = true;
      return;
    }

    if (response.authorizationPath) {
      this.redirectTo(
        this.authenticationService.buildOAuthAuthorizationUrl(response.authorizationPath)
      );
    }
  }

  private handleLoginProviderError(error: unknown, submittedEmail: string): void {
    if (
      error instanceof HttpErrorResponse
      && error.status === 404
      && this.emailControl.value === submittedEmail
    ) {
      this.accountNotFound = true;
    }
  }
}
